import React from 'react';

type DivProps = React.HTMLAttributes<HTMLDivElement>;

// Collapse — mirrors react-bootstrap <Collapse in>. Simple show/hide (grid-rows transition).
export const Collapse: React.FC<{ in?: boolean; className?: string; children: React.ReactNode }> = ({ in: open, className = '', children }) => (
  <div className={`tw-grid tw-transition-all tw-duration-200 ${open ? 'tw-grid-rows-[1fr] tw-opacity-100' : 'tw-grid-rows-[0fr] tw-opacity-0'} ${className}`}>
    <div className="tw-overflow-hidden">{children}</div>
  </div>
);

// ListGroup + Item
const LG_VARIANT: Record<string, string> = {
  primary: 'tw-bg-[#cfe2ff] tw-text-[#084298]',
  secondary: 'tw-bg-[#e2e3e5] tw-text-[#41464b]',
  success: 'tw-bg-[#d1e7dd] tw-text-[#0f5132]',
  danger: 'tw-bg-[#f8d7da] tw-text-[#842029]',
  warning: 'tw-bg-warning tw-text-warning-text',
  info: 'tw-bg-[#cff4fc] tw-text-[#055160]',
  light: 'tw-bg-surface-alt',
  dark: 'tw-bg-content tw-text-white',
};
interface ListGroupProps extends DivProps {
  variant?: 'flush' | string;
  flush?: boolean;
  horizontal?: boolean;
}
interface ListGroupType extends React.FC<ListGroupProps> {
  Item: React.FC<React.HTMLAttributes<HTMLElement> & { active?: boolean; action?: boolean; as?: React.ElementType; disabled?: boolean; variant?: string }>;
}
export const ListGroup = (({ variant, flush, horizontal, className = '', children, ...rest }: ListGroupProps) => {
  const isFlush = flush || variant === 'flush';
  return (
    <div
      className={`tw-flex ${horizontal ? 'tw-flex-row' : 'tw-flex-col'} ${isFlush ? '' : 'tw-rounded tw-border tw-border-line'} ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}) as ListGroupType;
ListGroup.Item = ({ active, action, as, variant, className = '', children, ...rest }) => {
  const Comp: React.ElementType = as || (action ? 'button' : 'div');
  return (
    <Comp
      className={`tw-border-b tw-border-line tw-px-3 tw-py-2 last:tw-border-b-0 ${
        active ? 'tw-bg-primary tw-text-white' : variant ? LG_VARIANT[variant] || '' : action ? 'tw-text-left hover:tw-bg-surface-hover' : ''
      } ${className}`}
      {...rest}
    >
      {children}
    </Comp>
  );
};

// Pagination + parts
interface PaginationType extends React.FC<React.HTMLAttributes<HTMLUListElement>> {
  Item: React.FC<React.HTMLAttributes<HTMLLIElement> & { active?: boolean; disabled?: boolean; onClick?: () => void }>;
  Prev: React.FC<{ disabled?: boolean; onClick?: () => void }>;
  Next: React.FC<{ disabled?: boolean; onClick?: () => void }>;
  First: React.FC<{ disabled?: boolean; onClick?: () => void }>;
  Last: React.FC<{ disabled?: boolean; onClick?: () => void }>;
  Ellipsis: React.FC<{ disabled?: boolean }>;
}
const pageBtn = 'tw-min-w-[2rem] tw-border tw-border-line tw-px-2 tw-py-1 tw-text-sm hover:tw-bg-surface-hover disabled:tw-opacity-50';
export const Pagination = (({ className = '', children, ...rest }: React.HTMLAttributes<HTMLUListElement>) => (
  <ul className={`tw-inline-flex -tw-space-x-px tw-rounded ${className}`} {...rest}>{children}</ul>
)) as PaginationType;
Pagination.Item = ({ active, disabled, onClick, className = '', children, ...rest }) => (
  <li {...rest}>
    <button type="button" disabled={disabled} onClick={onClick} className={`${pageBtn} ${active ? 'tw-bg-primary tw-text-white tw-border-primary' : 'tw-bg-surface tw-text-primary'} ${className}`}>
      {children}
    </button>
  </li>
);
const navBtn = (label: string) => ({ disabled, onClick }: { disabled?: boolean; onClick?: () => void }) => (
  <li><button type="button" disabled={disabled} onClick={onClick} className={`${pageBtn} tw-bg-surface tw-text-primary`}>{label}</button></li>
);
Pagination.Prev = navBtn('‹');
Pagination.Next = navBtn('›');
Pagination.First = navBtn('«');
Pagination.Last = navBtn('»');
Pagination.Ellipsis = () => <li><span className={`${pageBtn} tw-bg-surface tw-text-content-muted`}>…</span></li>;

// InputGroup + Text
interface InputGroupType extends React.FC<DivProps> {
  Text: React.FC<DivProps>;
}
export const InputGroup = (({ className = '', children, ...rest }: DivProps) => (
  <div className={`tw-flex tw-items-stretch [&>*:not(:first-child)]:tw-rounded-l-none [&>*:not(:last-child)]:tw-rounded-r-none ${className}`} {...rest}>{children}</div>
)) as InputGroupType;
InputGroup.Text = ({ className = '', children, ...rest }) => (
  <div className={`tw-flex tw-items-center tw-border tw-border-line-input tw-bg-surface-alt tw-px-3 tw-text-content-secondary ${className}`} {...rest}>{children}</div>
);
